import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Calendar, ChevronLeft, ChevronRight, Leaf } from "lucide-react";
import WhatsAppCTA from "../components/WhatsAppCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_IMAGES = [
  "/assets/projects/chi-chi-mar/01_dsc02713.jpg",
  "/assets/projects/kitchen/02_dsc03673.jpg",
  "/assets/projects/clements/02_render.jpg",
  "/assets/projects/jardin-nacho/02_dsc00531.jpg",
  "/assets/projects/casa-roca/img_20231127_114810.jpg"
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const [project, setProject] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await fetch(`${API}/projects/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
    } finally {
      setLoading(false);
    }
  };

  const images = project?.images?.length > 0 ? project.images : PLACEHOLDER_IMAGES;
  const beforeImages = project?.before_images || [];
  const afterImages = project?.after_images || [];
  const plants = project?.plants || [];

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="animate-pulse">
            <div className="h-8 bg-circulo-subtle w-32 mb-8" />
            <div className="aspect-video bg-circulo-subtle mb-8" />
            <div className="h-12 bg-circulo-subtle w-1/2 mb-4" />
            <div className="h-6 bg-circulo-subtle w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">
            {isEn ? "Project not found" : "Proyecto no encontrado"}
          </h1>
          <Link to="/portfolio" className="btn-secondary">
            {isEn ? "Back to portfolio" : "Volver al portfolio"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#F9F6F0]" data-testid="project-detail-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link 
            to="/portfolio" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
            data-testid="back-to-portfolio"
          >
            <ArrowLeft size={16} />
            {isEn ? "Back to portfolio" : "Volver al portfolio"}
          </Link>
        </motion.div>

        {/* Project Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <span className="tag mb-4">{project.category?.replace("_", " ")}</span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium mb-4">
            {project.title}
          </h1>
          <div className="flex items-center gap-6 text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-circulo-sand" />
              <span>{project.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-circulo-sand" />
              <span>{project.year}</span>
            </div>
          </div>
        </motion.div>

        {/* Main Gallery */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <div className="relative aspect-video overflow-hidden mb-4">
            <img
              src={images[currentImage]}
              alt={project.title}
              className="w-full h-full object-cover"
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  data-testid="prev-image"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  data-testid="next-image"
                >
                  <ChevronRight size={24} />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImage ? "bg-white" : "bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-4">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`flex-shrink-0 w-24 h-16 overflow-hidden border-2 transition-colors ${
                    index === currentImage ? "border-circulo-sand" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.section>

        {/* Project Description */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <h2 className="text-2xl font-serif mb-6">
            {isEn ? "About the Project" : "Sobre el Proyecto"}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {project.full_description ? (
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {project.full_description}
                </p>
              ) : (
                <div className="p-8 border border-dashed border-white/20 text-center">
                  <p className="text-muted-foreground">
                    {isEn ? "Project description coming soon..." : "Descripción del proyecto próximamente..."}
                  </p>
                </div>
              )}
            </div>
            <div className="p-6 bg-circulo-paper border border-white/10">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                {isEn ? "Project Details" : "Detalles del Proyecto"}
              </h3>
              <div className="space-y-4">
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">
                    {isEn ? "Location" : "Ubicación"}
                  </span>
                  <span>{project.location}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">
                    {isEn ? "Year" : "Año"}
                  </span>
                  <span>{project.year}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">
                    {isEn ? "Category" : "Categoría"}
                  </span>
                  <span className="capitalize">{project.category?.replace("_", " ")}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Before & After Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-20"
        >
          <h2 className="text-2xl font-serif mb-6">
            {isEn ? "Evolution: Before & After" : "Evolución: Antes y Después"}
          </h2>
          
          {beforeImages.length > 0 && afterImages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Before */}
              <div>
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  {isEn ? "Before" : "Antes"}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {beforeImages.map((img, index) => (
                    <div key={index} className="aspect-[4/3] overflow-hidden">
                      <img src={img} alt={`Before ${index + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* After */}
              <div>
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  {isEn ? "After" : "Después"}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {afterImages.map((img, index) => (
                    <div key={index} className="aspect-[4/3] overflow-hidden">
                      <img src={img} alt={`After ${index + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Before Placeholder */}
              <div>
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  {isEn ? "Before" : "Antes"}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="aspect-[4/3] bg-circulo-subtle border border-dashed border-white/20 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        {isEn ? `Before ${i}` : `Antes ${i}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* After Placeholder */}
              <div>
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  {isEn ? "After" : "Después"}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="aspect-[4/3] bg-circulo-subtle border border-dashed border-white/20 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        {isEn ? `After ${i}` : `Después ${i}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.section>

        {/* Plant Palette Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <Leaf className="w-6 h-6 text-circulo-sand" />
            <h2 className="text-2xl font-serif">
              {isEn ? "Plant Palette" : "Paleta Vegetal"}
            </h2>
          </div>
          
          {plants.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {plants.map((plant, index) => (
                <div key={index} className="text-center">
                  <div className="aspect-square overflow-hidden mb-3 bg-circulo-subtle">
                    {plant.image_url ? (
                      <img src={plant.image_url} alt={plant.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Leaf className="w-8 h-8 text-circulo-sand/30" />
                      </div>
                    )}
                  </div>
                  <h4 className="text-sm font-medium">{plant.name}</h4>
                  {plant.scientific_name && (
                    <p className="text-xs text-muted-foreground italic">{plant.scientific_name}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="text-center">
                  <div className="aspect-square bg-circulo-subtle border border-dashed border-white/20 flex items-center justify-center mb-3">
                    <Leaf className="w-8 h-8 text-circulo-sand/20" />
                  </div>
                  <div className="h-4 bg-circulo-subtle w-2/3 mx-auto mb-2" />
                  <div className="h-3 bg-circulo-subtle w-1/2 mx-auto" />
                </div>
              ))}
            </div>
          )}
        </motion.section>

        {/* CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <WhatsAppCTA
            source={`project_detail_${project?.title}`}
            title={isEn ? "Want something similar?" : "¿Te interesa un proyecto similar?"}
            subtitle={isEn
              ? "Tell us about your space via WhatsApp. Direct response, no forms."
              : "Cuéntanos sobre tu espacio por WhatsApp. Respuesta directa, sin formularios."}
            buttonText={isEn ? "Chat on WhatsApp" : "Escribir por WhatsApp"}
            message={`Hola, vi el proyecto ${project?.title || ''} en su web y me interesa algo similar para mi propiedad.`}
            variant="dark"
          />
        </motion.section>
      </div>
    </div>
  );
}
